import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Link } from 'react-router-dom';
import { Gamepad2 } from 'lucide-react';

interface SimilarGame {
    id: number;
    name: string;
    slug: string;
    image_url?: string;
    thanksCount: number;
    downloadCount: number;
    genres: Array<{ id: number; name: string }>;
    tags?: Array<{ id: number; name_hu: string; name_en: string }>;
}

interface GameSimilarGamesBoxProps {
    gameId: number;
    gameSlug: string;
    limit?: number;
}

const GameSimilarGamesBox: React.FC<GameSimilarGamesBoxProps> = ({ gameId, gameSlug, limit = 6 }) => {
    const [similarGames, setSimilarGames] = useState<SimilarGame[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const fetchSimilarGames = async () => {
            try {
                setLoading(true);
                const response = await fetch(`/api/games/${gameSlug}/similar_games?limit=${limit}`);

                if (!response.ok) {
                    throw new Error('Nem sikerült az adatok lekérése');
                }

                const data = await response.json();
                setSimilarGames(data);
                setError(null);
            } catch (err) {
                console.error("Hasonló játékok lekérése sikertelen:", err);
                setError(err instanceof Error ? err.message : 'Hiba történt');
                setSimilarGames([]);
            } finally {
                setLoading(false);
            }
        };

        if (gameSlug) {
            fetchSimilarGames();
        }
    }, [gameSlug, limit]);

    if (loading) {
        return (
            <Card className="bg-card text-card-foreground border-border shadow-lg">
                <CardHeader>
                    <CardTitle className="text-xl font-bold text-primary flex items-center gap-2">
                        <Gamepad2 size={20} /> Hasonló játékok
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="flex justify-center items-center py-4">
                        <p className="text-muted-foreground">Betöltés...</p>
                    </div>
                </CardContent>
            </Card>
        );
    }

    if (error || similarGames.length === 0) {
        return null;
    }

    return (
        <Card className="bg-card text-card-foreground border-border shadow-lg">
            <CardHeader>
                <CardTitle className="text-xl font-bold text-primary flex items-center gap-2">
                    <Gamepad2 size={20} /> Hasonló játékok
                </CardTitle>
            </CardHeader>
            <CardContent>
                <div className="overflow-x-auto scrollbar-hide">
                    <div className="flex gap-4 pb-2 min-w-min">
                        {similarGames.map((game) => (
                            <Link
                                key={game.id}
                                to={`/games/${game.slug}`}
                                className="group flex-shrink-0 w-48"
                            >
                                <div className="flex flex-col h-full rounded-lg hover:shadow-lg transition-shadow p-2">
                                    {game.image_url && (
                                        <div className="w-full h-32 rounded overflow-hidden bg-muted mb-3">
                                            <img
                                                src={game.image_url}
                                                alt={game.name}
                                                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-200"
                                            />
                                        </div>
                                    )}
                                    <h4 className="text-sm font-semibold text-foreground group-hover:text-primary transition-colors line-clamp-2 mb-2">
                                        {game.name}
                                    </h4>
                                    {game.genres && game.genres.length > 0 && (
                                        <div className="flex flex-wrap gap-1 mb-2">
                                            {game.genres.slice(0, 4).map((genre) => (
                                                <span
                                                    key={`genre-${genre.id}`}
                                                    className="text-xs bg-secondary/50 text-secondary-foreground px-2 py-0.5 rounded whitespace-nowrap"
                                                >
                                                    {genre.name}
                                                </span>
                                            ))}
                                        </div>
                                    )}
                                    {game.tags && game.tags.length > 0 && (
                                        <div className="flex flex-wrap gap-1">
                                            {game.tags.slice(0, 5).map((tag) => (
                                                <span
                                                    key={tag.id}
                                                    className="text-xs bg-primary/20 text-primary px-2 py-0.5 rounded whitespace-nowrap"
                                                >
                                                    {tag.name_hu || tag.name_en}
                                                </span>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </Link>
                        ))}
                    </div>
                </div>
            </CardContent>
        </Card>
    );
};

export default GameSimilarGamesBox;
